import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, Ban, Trash2, ShieldCheck, User as UserIcon, CheckCircle2, Link2, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import BlogBadge from "../../components/BlogBadge";
import PreviewContent from "../../components/PreviewContent";

const FEATURES = [["generate", "Gerar conteúdo", "Generate content"], ["dubbing", "Dobragem de vídeos", "Video dubbing"]];

export default function UsersTab() {
  const { t, lang } = useLang();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [canViewAll, setCanViewAll] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [emailSent, setEmailSent] = useState(null);
  const [viewDrafts, setViewDrafts] = useState(null);
  const [userDrafts, setUserDrafts] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      const { data } = await api.post("/users/invite", { email, name, role: "invited", can_view_all_drafts: canViewAll });
      const link = data.invite_url || `${window.location.origin}/accept-invite?token=${data.invite_token}`;
      setInviteLink(link);
      setEmailSent(!!data.email_sent);
      toast.success(data.email_sent ? t("Convite enviado por email!", "Invite sent by email!") : t("Convite criado (email não enviado)", "Invite created (email not sent)"));
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(t("Copiado!", "Copied!")); };
  const [savingId, setSavingId] = useState(null);
  const togglePerm = async (u, f, v) => {
    const next = v ? [...new Set([...(u.permissions || []), f])] : (u.permissions || []).filter((x) => x !== f);
    setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, permissions: next } : x)));
    setSavingId(u.id);
    try { await api.patch(`/users/${u.id}`, { permissions: next }); toast.success(t("Atualizado", "Updated")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { try { setUsers((await api.get("/users")).data); } catch { /* keep optimistic state */ } setSavingId(null); }
  };
  const patch = async (u, body) => { try { await api.patch(`/users/${u.id}`, body); await load(); toast.success(t("Atualizado", "Updated")); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const revoke = async (u) => { try { await api.post(`/users/${u.id}/revoke`); await load(); toast.success(t("Revogado", "Revoked")); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const remove = async (u) => { try { await api.delete(`/users/${u.id}`); await load(); toast.success(t("Removido", "Removed")); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const openDrafts = async (u) => {
    setViewDrafts(u);
    try { const { data } = await api.get("/drafts"); setUserDrafts(data.filter((d) => d.created_by === u.id)); }
    catch { setUserDrafts([]); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h2 className="font-heading text-2xl font-bold mr-auto">{t("Utilizadores", "Users")}</h2>
        <Button data-testid="invite-user-btn" className="gap-2" onClick={() => { setShowInvite(true); setInviteLink(""); setEmailSent(null); setEmail(""); setName(""); setCanViewAll(false); }}>
          <UserPlus className="h-4 w-4" />{t("Convidar utilizador", "Invite user")}
        </Button>
      </div>

      {loading ? <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p> : (
        <div className="rounded-2xl border border-white/10 bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">{t("Utilizador", "User")}</th>
                <th className="px-4 py-3">{t("Papel", "Role")}</th>
                <th className="px-4 py-3">{t("Estado", "Status")}</th>
                <th className="px-4 py-3">{t("Vê todos drafts", "Sees all drafts")}</th>
                <th className="px-4 py-3">{t("Acessos", "Access")}</th>
                <th className="px-4 py-3 text-right">{t("Ações", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-white/10">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                    {!u.invite_accepted && u.invite_token && (
                      <button onClick={() => copy(`${window.location.origin}/accept-invite?token=${u.invite_token}`)} className="mt-1 inline-flex items-center gap-1 text-xs text-primary"><Link2 className="h-3 w-3" />{t("copiar link de convite", "copy invite link")}</button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 font-semibold">{u.role === "admin" ? <ShieldCheck className="h-4 w-4 text-primary" /> : <UserIcon className="h-4 w-4 text-muted-foreground" />}{u.role === "admin" ? "Admin" : t("Convidado", "Invited")}</span>
                  </td>
                  <td className="px-4 py-3">
                    {!u.active ? <span className="text-red-400 font-semibold">{t("Bloqueado", "Blocked")}</span>
                      : u.invite_accepted ? <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-4 w-4" />{t("Ativo", "Active")}</span>
                      : <span className="text-amber-400">{t("Convite pendente", "Invite pending")}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Switch data-testid={`user-viewall-${u.id}`} checked={u.can_view_all_drafts} disabled={u.role === "admin"} onCheckedChange={(v) => patch(u, { can_view_all_drafts: v })} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="grid gap-1.5">
                      {FEATURES.map(([f, pt, en]) => (
                        <label key={f} className="flex items-center gap-2 text-xs">
                          <Switch data-testid={`user-perm-${f}-${u.id}`} checked={u.role === "admin" || (u.permissions || []).includes(f)} disabled={u.role === "admin" || savingId === u.id}
                            onCheckedChange={(v) => togglePerm(u, f, v)} />
                          {t(pt, en)}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="ghost" className="gap-1.5" data-testid={`view-drafts-${u.id}`} onClick={() => openDrafts(u)}><Eye className="h-4 w-4" /></Button>
                      {u.role !== "admin" && (
                        <>
                          {u.active
                            ? <Button size="sm" variant="ghost" className="text-amber-400" data-testid={`block-${u.id}`} onClick={() => patch(u, { active: false })}><Ban className="h-4 w-4" /></Button>
                            : <Button size="sm" variant="ghost" className="text-emerald-400" data-testid={`unblock-${u.id}`} onClick={() => patch(u, { active: true })}><CheckCircle2 className="h-4 w-4" /></Button>}
                          {!u.invite_accepted && <Button size="sm" variant="ghost" className="text-xs" data-testid={`revoke-${u.id}`} onClick={() => revoke(u)}>{t("Revogar", "Revoke")}</Button>}
                          <Button size="sm" variant="ghost" className="text-destructive" data-testid={`remove-${u.id}`} onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Convidar utilizador", "Invite user")}</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-3">
              <div className={`rounded-lg border p-3 text-sm ${emailSent ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="invite-email-status">
                {emailSent
                  ? t("✅ Email de convite enviado para " + email, "✅ Invite email sent to " + email)
                  : t("⚠️ Não foi possível enviar o email — partilhe o link manualmente.", "⚠️ Could not send the email — share the link manually.")}
              </div>
              <p className="text-sm text-muted-foreground">{t("Link de convite (também enviado por email). O colaborador define a palavra-passe e ganha acesso ao painel de geração (sem publicar).", "Invite link (also emailed). The collaborator sets a password and gets access to the generation panel (no publishing).")}</p>
              <div className="flex gap-2">
                <Input data-testid="invite-link-output" readOnly value={inviteLink} className="font-mono text-xs" />
                <Button className="gap-1.5" onClick={() => copy(inviteLink)}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div><Label>Email</Label><Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" /></div>
              <div><Label>{t("Nome", "Name")}</Label><Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
              <label className="flex items-center justify-between text-sm"><span>{t("Pode ver drafts de outros", "Can view others' drafts")}</span><Switch data-testid="invite-viewall-switch" checked={canViewAll} onCheckedChange={setCanViewAll} /></label>
            </div>
          )}
          <DialogFooter>
            {inviteLink ? <Button onClick={() => setShowInvite(false)}>{t("Concluir", "Done")}</Button>
              : <Button data-testid="send-invite-btn" onClick={invite} disabled={!email}>{t("Gerar convite", "Generate invite")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View drafts dialog */}
      <Dialog open={!!viewDrafts} onOpenChange={(o) => !o && setViewDrafts(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("Drafts de", "Drafts by")} {viewDrafts?.name || viewDrafts?.email}</DialogTitle></DialogHeader>
          {userDrafts.length === 0 ? <p className="text-sm text-muted-foreground">{t("Sem rascunhos.", "No drafts.")}</p> : (
            <div className="space-y-2">
              {userDrafts.map((d) => (
                <div key={d.id} className="rounded-lg border border-white/10 bg-background/50 p-3">
                  <div className="flex items-center gap-2"><BlogBadge blog={d.blog} /><span className="text-xs text-muted-foreground">{d.type} · {d.status}</span></div>
                  <div className="mt-1 font-semibold text-sm">{d.title}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
